import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import {
  Button,
  DeleteDialog,
  LoadingPromiseButtonAdvanced,
  Modal,
  ModalInner,
  usePrompt,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';
import { MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS } from '@pflegenavi/shared/constants';

interface ReceiptBatchActionsProps {
  blockSubmit: boolean;
  allowNavigateAway: boolean;
  handleDeleteBatch?: VoidFunction;
  unblockRef?: React.MutableRefObject<() => void>;
}

export const ReceiptBatchActionButtons: FC<ReceiptBatchActionsProps> = ({
  blockSubmit,
  allowNavigateAway,
  handleDeleteBatch,
  unblockRef,
}) => {
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [dirty, nursingHomeId, receipts, generateReceipts] =
    useReceiptBatchStore(
      useShallow((state) => [
        state?.formik?.dirty,
        state?.formik?.values?.nursingHomeId,
        state?.formik?.values?.receipts,
        state?.formik?.values?.generateReceipts,
      ])
    );

  const isThereEntryThatExceedsMaxAmountForGeneratedReceipts = useMemo(() => {
    if (!generateReceipts) {
      return false;
    }
    return receipts?.some((receipt) => {
      const entrySum = receipt?.lineItems?.reduce(
        (sum, entry) => sum + (entry?.amount ?? 0),
        0
      );
      return entrySum > MAX_ENTRY_TOTAL_AMOUNT_FOR_RECEIPT_GENERATION_IN_CENTS;
    });
  }, [generateReceipts, receipts]);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const unblock = usePrompt(
    t('confirm-page-leave'),
    allowNavigateAway ? false : dirty
  );
  if (unblockRef) {
    unblockRef.current = unblock.unblock;
  }

  return (
    <ReceiptBatchActionButtonsUI
      deleteModalOpen={deleteModalOpen}
      setDeleteModalOpen={setDeleteModalOpen}
      cancelModalOpen={cancelModalOpen}
      setCancelModalOpen={setCancelModalOpen}
      blockSubmit={
        blockSubmit ||
        nursingHomeId === undefined ||
        Boolean(isThereEntryThatExceedsMaxAmountForGeneratedReceipts)
      }
      handleDeleteBatch={handleDeleteBatch}
    />
  );
};

export interface ReceiptBatchActionsUIProps {
  deleteModalOpen: boolean;
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cancelModalOpen: boolean;
  setCancelModalOpen: (open: boolean) => void;
  blockSubmit: boolean;
  handleDeleteBatch?: VoidFunction;
}

export const ReceiptBatchActionButtonsUI: FC<ReceiptBatchActionsUIProps> = ({
  deleteModalOpen,
  setDeleteModalOpen,
  cancelModalOpen,
  setCancelModalOpen,
  blockSubmit,
  handleDeleteBatch,
}) => {
  const { t } = useTranslation();

  const [dirty, isSubmitting, resetForm, submitForm] = useReceiptBatchStore(
    useShallow((state) => [
      state.formik?.dirty,
      state.formik?.isSubmitting,
      state.formik?.resetForm,
      state.formik?.submitForm,
    ])
  );

  return (
    <Stack
      direction="row"
      justifyContent={handleDeleteBatch ? 'space-between' : 'flex-end'}
      spacing={1}
      sx={{ mt: 3 }}
    >
      {handleDeleteBatch && (
        <>
          <Button
            onClick={() => setDeleteModalOpen(true)}
            color="error"
            variant="text"
          >
            {t('receipts.batch.form.delete-batch')}
          </Button>
          <DeleteDialog
            open={deleteModalOpen}
            handleDelete={handleDeleteBatch}
            setDeleteDialogOpen={setDeleteModalOpen}
            title={t('receipts.batch.form.delete-batch')}
            confirmationMessage={t('receipts.batch.form.confirm-delete-text')}
          />
        </>
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {dirty && (
          <>
            <Button variant="outlined" onClick={() => setCancelModalOpen(true)}>
              {t('receipts.batch.form.abort-changes')}
            </Button>
            <ConfirmCancelModal
              open={cancelModalOpen}
              setOpen={setCancelModalOpen}
              resetForm={resetForm}
            />
          </>
        )}
        <LoadingPromiseButtonAdvanced
          disabled={isSubmitting || !dirty || blockSubmit}
          variant="contained"
          onClick={async () => await submitForm?.()}
        >
          {t('actions.save')}
        </LoadingPromiseButtonAdvanced>
      </Stack>
    </Stack>
  );
};

interface ConfirmCancelModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  resetForm?: () => void;
}

export const ConfirmCancelModal: FC<ConfirmCancelModalProps> = ({
  open,
  setOpen,
  resetForm,
}) => {
  const { t } = useTranslation();

  const handleConfirmCancel = () => {
    resetForm?.();
    setOpen(false);
  };
  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <ModalInner>
        <Box sx={{ width: 450 }}>
          <Card sx={{ p: 3 }}>
            <Typography sx={{ mb: 2 }}>
              {t('receipts.batch.form.abort-changes-confirmation-text')}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                {t('receipts.batch.form.abort-changes-no')}
              </Button>

              <Button
                color="error"
                variant="contained"
                onClick={handleConfirmCancel}
              >
                {t('receipts.batch.form.abort-changes-yes')}
              </Button>
            </Stack>
          </Card>
        </Box>
      </ModalInner>
    </Modal>
  );
};
